import { Box, Button } from "@mui/material";
import {
  createFileRoute,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { appContentGutter } from "@/design-tokens/layout";
import {
  ConnectorHeaderActionsContext,
  type ConnectorHeaderActions,
  DEFAULT_CONNECTOR_HEADER_ACTIONS,
} from "@/features/Connectors/hooks/useConnectorHeaderActions";
import { MuiTab } from "@/orion/MuiComponents/MuiTab";
import { MuiTabs } from "@/orion/MuiComponents/MuiTabs";
import { OrionBackButton } from "@/orion/OrionComponents/OrionBackButton";
import { OrionPageHeader } from "@/orion/OrionComponents/OrionPageHeader";
import theme from "@/theme";

export const Route = createFileRoute("/_auth/settings/$siteId/connectors")({
  component: ConnectorsLayout,
});

const TABS = [
  { value: "installed", label: "INSTALLED CONNECTORS" },
  { value: "all", label: "ALL CONNECTORS" },
] as const;

function ConnectorsLayout() {
  const { siteId = "", connectorId } = useParams({ strict: false });
  const navigate = useNavigate();
  const location = useLocation();
  const tabValue = location.pathname.endsWith("/all") ? 1 : 0;
  const [headerActions, setHeaderActions] = useState<ConnectorHeaderActions>(
    DEFAULT_CONNECTOR_HEADER_ACTIONS,
  );

  const isConfigPage = Boolean(connectorId);

  useEffect(() => {
    if (!isConfigPage) {
      setHeaderActions(DEFAULT_CONNECTOR_HEADER_ACTIONS);
    }
  }, [isConfigPage]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const tab = TABS[newValue];
    if (tab) {
      void navigate({
        to: `/settings/${siteId}/connectors/${tab.value}`,
      });
    }
  };

  const header = (
    <Box
      sx={{
        backgroundColor: theme.palette.background["paper-elevation-1"],
      }}
    >
      <Box sx={{ px: appContentGutter }}>
        <OrionPageHeader
          paddingX={0}
          paddingY={6}
          breadcrumbs={[{ label: "Settings" }]}
          title="Connectors"
          actions={
            isConfigPage ? (
              <Box sx={{ display: "flex", gap: 3 }}>
                <OrionBackButton
                  variant="outlined"
                  fallbackTo="/settings/$siteId/connectors/installed"
                  fallbackParams={{ siteId }}
                  sx={{
                    minWidth: 97,
                    backgroundColor: "primary.contrastText",
                  }}
                />
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={headerActions.onCancel}
                  disabled={headerActions.cancelDisabled}
                  sx={(theme) => ({
                    minWidth: 97,
                    backgroundColor:
                      theme.palette.secondary.customStateColor
                        ?.outlinedBorder ?? theme.palette.secondary.light,
                    "&.Mui-disabled": {
                      borderColor: theme.palette.action.disabledBackground,
                      color: theme.palette.action.disabled,
                      backgroundColor: theme.palette.action.disabledBackground,
                    },
                  })}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={headerActions.onSave}
                  disabled={headerActions.saveDisabled}
                  sx={{
                    minWidth: 97,
                  }}
                >
                  Save
                </Button>
              </Box>
            ) : null
          }
        />
      </Box>
    </Box>
  );

  const contextValue = useMemo(
    () => ({
      setHeaderActions,
    }),
    [setHeaderActions],
  );

  return (
    <ConnectorHeaderActionsContext.Provider value={contextValue}>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          flex: "1",
          maxHeight: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.palette.background["paper-elevation-0"],
        }}
      >
        {header}
        {isConfigPage ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              px: appContentGutter,
            }}
          >
            <Outlet />
          </Box>
        ) : (
          <>
            <Box sx={{ px: appContentGutter, pt: 0 }}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <MuiTabs
                  value={tabValue}
                  onChange={handleTabChange}
                  aria-label="Connectors tabs"
                >
                  {TABS.map((tab) => (
                    <MuiTab key={tab.value} label={tab.label} />
                  ))}
                </MuiTabs>
              </Box>
            </Box>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                px: appContentGutter,
              }}
            >
              <Outlet />
            </Box>
          </>
        )}
      </Box>
    </ConnectorHeaderActionsContext.Provider>
  );
}
